/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { http, HttpResponse } from 'msw';

const wmtsCapabilities = `<?xml version="1.0"?>
<Capabilities xmlns="http://www.opengis.net/wmts/1.0" xmlns:ows="http://www.opengis.net/ows/1.1" xmlns:xlink="http://www.w3.org/1999/xlink" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:gml="http://www.opengis.net/gml" xsi:schemaLocation="http://www.opengis.net/wmts/1.0 http://schemas.opengis.net/wmts/1.0/wmtsGetCapabilities_response.xsd" version="1.0.0">
  <ows:ServiceIdentification>
    <ows:Title></ows:Title>
    <ows:Abstract></ows:Abstract>
    <ows:ServiceType>OGC WMTS</ows:ServiceType>
    <ows:ServiceTypeVersion>1.0.0</ows:ServiceTypeVersion>
    <ows:Fees>none</ows:Fees>
    <ows:AccessConstraints>none</ows:AccessConstraints>
  </ows:ServiceIdentification>
  <ows:OperationsMetadata>
    <ows:Operation name="GetCapabilities">
      <ows:DCP>
        <ows:HTTP>
          <ows:Get xlink:href="https://my-tile-based-url.com/wmts?">
            <ows:Constraint name="GetEncoding">
              <ows:AllowedValues>
                <ows:Value>RESTful</ows:Value>
              </ows:AllowedValues>
            </ows:Constraint>
          </ows:Get>
        </ows:HTTP>
      </ows:DCP>
    </ows:Operation>
    <ows:Operation name="GetTile">
      <ows:DCP>
        <ows:HTTP>
          <ows:Get xlink:href="https://my-tile-based-url.com/wmts?">
            <ows:Constraint name="GetEncoding">
              <ows:AllowedValues>
                <ows:Value>RESTful</ows:Value>
              </ows:AllowedValues>
            </ows:Constraint>
          </ows:Get>
        </ows:HTTP>
      </ows:DCP>
    </ows:Operation>
    <ows:Operation name="GetFeatureInfo">
      <ows:DCP>
        <ows:HTTP>
          <ows:Get xlink:href="https://my-tile-based-url.com/wmts?">
            <ows:Constraint name="GetEncoding">
              <ows:AllowedValues>
                <ows:Value>RESTful</ows:Value>
              </ows:AllowedValues>
            </ows:Constraint>
          </ows:Get>
        </ows:HTTP>
      </ows:DCP>
    </ows:Operation>
  </ows:OperationsMetadata>
  <Contents>
    <Layer>
      <ows:Title>Cached Wmts Layer</ows:Title>
      <ows:Abstract></ows:Abstract>
      <ows:WGS84BoundingBox>
        <ows:LowerCorner>0.0 48.89530347325975</ows:LowerCorner>
        <ows:UpperCorner>10.856451909596997 55.97360000935789</ows:UpperCorner>
      </ows:WGS84BoundingBox>
      <ows:Identifier>precipitation_real_time</ows:Identifier>
      <Style isDefault="true">
        <ows:Identifier>default</ows:Identifier>
      </Style>
      <Format>image/png</Format>
      <Dimension>
        <ows:Identifier>Time</ows:Identifier>
        <Default>2025-12-15T13:30:00Z</Default>
        <Value>2025-12-08T13:35:00Z</Value>
        <Value>2025-12-08T13:40:00Z</Value>
        <Value>2025-12-08T13:45:00Z</Value>
        <Value>2025-12-08T13:50:00Z</Value>
        <Value>2025-12-08T13:55:00Z</Value>
        <Value>2025-12-08T14:00:00Z</Value>
        <Value>2025-12-08T14:05:00Z</Value>
        <Value>2025-12-08T14:10:00Z</Value>
        <Value>2025-12-08T14:15:00Z</Value>
        <Value>2025-12-08T14:20:00Z</Value>
        <Value>2025-12-08T14:25:00Z</Value>
        <Value>2025-12-08T14:30:00Z</Value>
      </Dimension>
      <TileMatrixSetLink>
        <TileMatrixSet>EPSG:3857</TileMatrixSet>
      </TileMatrixSetLink>
      <ResourceURL format="image/png" resourceType="tile"
        template="https://my-tile-based-url.com/wmts?precipitation_real_time/{Time}/{TileMatrixSet}/{TileMatrix}/{TileRow}/{TileCol}.png"/>
    </Layer>
    <TileMatrixSet>
      <ows:Identifier>EPSG:3857</ows:Identifier>
      <ows:SupportedCRS>urn:ogc:def:crs:EPSG:3857</ows:SupportedCRS>
      <TileMatrix>
        <ows:Identifier>00</ows:Identifier>
        <ScaleDenominator>1542912.7849178575</ScaleDenominator>
        <TopLeftCorner>0.0 7553161.958695</TopLeftCorner>
        <TileWidth>3000</TileWidth>
        <TileHeight>3000</TileHeight>
        <MatrixWidth>1</MatrixWidth>
        <MatrixHeight>1</MatrixHeight>
      </TileMatrix>
      <TileMatrix>
        <ows:Identifier>01</ows:Identifier>
        <ScaleDenominator>771456.3924589287</ScaleDenominator>
        <TopLeftCorner>0.0 7553161.958695</TopLeftCorner>
        <TileWidth>3000</TileWidth>
        <TileHeight>3000</TileHeight>
        <MatrixWidth>2</MatrixWidth>
        <MatrixHeight>2</MatrixHeight>
      </TileMatrix>
      <TileMatrix>
        <ows:Identifier>02</ows:Identifier>
        <ScaleDenominator>385728.1962294644</ScaleDenominator>
        <TopLeftCorner>0.0 7553161.958695</TopLeftCorner>
        <TileWidth>3000</TileWidth>
        <TileHeight>3000</TileHeight>
        <MatrixWidth>4</MatrixWidth>
        <MatrixHeight>4</MatrixHeight>
      </TileMatrix>
    </TileMatrixSet>
  </Contents>
  <ServiceMetadataURL xlink:href="https://my-tile-based-url.com/wmts?1.0.0/WMTSCapabilities.xml"/>
</Capabilities>`;

export const wmtsResponseGetCapabilities = {
  Capabilities: {
    attr: {
      xmlns: 'http://www.opengis.net/wmts/1.0',
      'xmlns:ows': 'http://www.opengis.net/ows/1.1',
      'xmlns:xlink': 'http://www.w3.org/1999/xlink',
      'xmlns:xsi': 'http://www.w3.org/2001/XMLSchema-instance',
      'xmlns:gml': 'http://www.opengis.net/gml',
      'xsi:schemaLocation':
        'http://www.opengis.net/wmts/1.0 http://schemas.opengis.net/wmts/1.0/wmtsGetCapabilities_response.xsd',
      version: '1.0.0',
    },
    ServiceIdentification: {
      attr: {},
      Title: { attr: {} },
      Abstract: { attr: {} },
      ServiceType: { attr: {}, value: 'OGC WMTS' },
      ServiceTypeVersion: { attr: {}, value: '1.0.0' },
      Fees: { attr: {}, value: 'none' },
      AccessConstraints: { attr: {}, value: 'none' },
    },
    OperationsMetadata: {
      attr: {},
      Operation: [
        {
          attr: { name: 'GetCapabilities' },
          DCP: {
            attr: {},
            HTTP: {
              attr: {},
              Get: {
                attr: {
                  'xlink:href': 'https://my-tile-based-url.com/wmts?',
                },
                Constraint: {
                  attr: { name: 'GetEncoding' },
                  AllowedValues: {
                    attr: {},
                    Value: { attr: {}, value: 'RESTful' },
                  },
                },
              },
            },
          },
        },
        {
          attr: { name: 'GetTile' },
          DCP: {
            attr: {},
            HTTP: {
              attr: {},
              Get: {
                attr: {
                  'xlink:href': 'https://my-tile-based-url.com/wmts?',
                },
                Constraint: {
                  attr: { name: 'GetEncoding' },
                  AllowedValues: {
                    attr: {},
                    Value: { attr: {}, value: 'RESTful' },
                  },
                },
              },
            },
          },
        },
        {
          attr: { name: 'GetFeatureInfo' },
          DCP: {
            attr: {},
            HTTP: {
              attr: {},
              Get: {
                attr: {
                  'xlink:href': 'https://my-tile-based-url.com/wmts?',
                },
                Constraint: {
                  attr: { name: 'GetEncoding' },
                  AllowedValues: {
                    attr: {},
                    Value: { attr: {}, value: 'RESTful' },
                  },
                },
              },
            },
          },
        },
      ],
    },
    Contents: {
      attr: {},
      Layer: {
        attr: {},
        Title: { attr: {}, value: 'Cached Wmts Layer' },
        Abstract: { attr: {} },
        WGS84BoundingBox: {
          attr: {},
          LowerCorner: { attr: {}, value: '0.0 48.89530347325975' },
          UpperCorner: {
            attr: {},
            value: '10.856451909596997 55.97360000935789',
          },
        },
        Identifier: { attr: {}, value: 'precipitation_real_time' },
        Style: {
          attr: { isDefault: 'true' },
          Identifier: { attr: {}, value: 'default' },
        },
        Format: { attr: {}, value: 'image/png' },
        Dimension: {
          attr: {},
          Identifier: { attr: {}, value: 'Time' },
          Default: { attr: {}, value: '2025-12-15T13:30:00Z' },
          Value: [
            { attr: {}, value: '2025-12-08T13:35:00Z' },
            { attr: {}, value: '2025-12-08T13:40:00Z' },
            { attr: {}, value: '2025-12-08T13:45:00Z' },
            { attr: {}, value: '2025-12-08T13:50:00Z' },
            { attr: {}, value: '2025-12-08T13:55:00Z' },
            { attr: {}, value: '2025-12-08T14:00:00Z' },
            { attr: {}, value: '2025-12-08T14:05:00Z' },
            { attr: {}, value: '2025-12-08T14:10:00Z' },
            { attr: {}, value: '2025-12-08T14:15:00Z' },
            { attr: {}, value: '2025-12-08T14:20:00Z' },
            { attr: {}, value: '2025-12-08T14:25:00Z' },
            { attr: {}, value: '2025-12-08T14:30:00Z' },
          ],
        },
        TileMatrixSetLink: {
          attr: {},
          TileMatrixSet: { attr: {}, value: 'EPSG:3857' },
        },
        ResourceURL: {
          attr: {
            format: 'image/png',
            resourceType: 'tile',
            template:
              'https://my-tile-based-url.com/wmts?precipitation_real_time/{Time}/{TileMatrixSet}/{TileMatrix}/{TileRow}/{TileCol}.png',
          },
        },
      },
      TileMatrixSet: {
        attr: {},
        Identifier: { attr: {}, value: 'EPSG:3857' },
        SupportedCRS: { attr: {}, value: 'urn:ogc:def:crs:EPSG:3857' },
        TileMatrix: [
          {
            attr: {},
            Identifier: { attr: {}, value: '00' },
            ScaleDenominator: { attr: {}, value: '1542912.7849178575' },
            TopLeftCorner: { attr: {}, value: '0.0 7553161.958695' },
            TileWidth: { attr: {}, value: '3000' },
            TileHeight: { attr: {}, value: '3000' },
            MatrixWidth: { attr: {}, value: '1' },
            MatrixHeight: { attr: {}, value: '1' },
          },
          {
            attr: {},
            Identifier: { attr: {}, value: '01' },
            ScaleDenominator: { attr: {}, value: '771456.3924589287' },
            TopLeftCorner: { attr: {}, value: '0.0 7553161.958695' },
            TileWidth: { attr: {}, value: '3000' },
            TileHeight: { attr: {}, value: '3000' },
            MatrixWidth: { attr: {}, value: '2' },
            MatrixHeight: { attr: {}, value: '2' },
          },
          {
            attr: {},
            Identifier: { attr: {}, value: '02' },
            ScaleDenominator: { attr: {}, value: '385728.1962294644' },
            TopLeftCorner: { attr: {}, value: '0.0 7553161.958695' },
            TileWidth: { attr: {}, value: '3000' },
            TileHeight: { attr: {}, value: '3000' },
            MatrixWidth: { attr: {}, value: '4' },
            MatrixHeight: { attr: {}, value: '4' },
          },
        ],
      },
    },
    ServiceMetadataURL: {
      attr: {
        'xlink:href':
          'https://my-tile-based-url.com/wmts?1.0.0/WMTSCapabilities.xml',
      },
    },
  },
};

export const wmtsResponseLayerList = [
  {
    abstract: '',
    dimensions: [
      {
        currentValue: '2025-12-15T13:30:00Z',
        defaultValue: '2025-12-15T13:30:00Z',
        name: 'Time',
        units: 'ISO8601',
        values:
          '2025-12-08T13:35:00Z,2025-12-08T13:40:00Z,2025-12-08T13:45:00Z,2025-12-08T13:50:00Z,2025-12-08T13:55:00Z,2025-12-08T14:00:00Z,2025-12-08T14:05:00Z,2025-12-08T14:10:00Z,2025-12-08T14:15:00Z,2025-12-08T14:20:00Z,2025-12-08T14:25:00Z,2025-12-08T14:30:00Z',
      },
    ],
    leaf: true,
    name: 'precipitation_real_time',
    path: [],
    ogctype: 'WMTS',
    styles: [
      { abstract: '', legendURL: '', name: 'default', title: 'default' },
    ],
    title: 'Cached Wmts Layer',
  },
];

export const fakeWMTSLayerApiHandlers = [
  http.get('https://my-tile-based-url.com/wmts', ({ request }) => {
    const url = new URL(request.url);
    const serviceKey = url.searchParams.get('service');
    const requestKey = url.searchParams.get('request');
    if (serviceKey === 'wmts' && requestKey === 'getcapabilities') {
      return HttpResponse.text(wmtsCapabilities);
    }
    return HttpResponse.json(
      {
        error: `WMTS GetCapabilities not found (got: [${serviceKey}]/[${requestKey}]`,
      },
      { status: 404 },
    );
  }),
];
